import { collection, getDocs, query, where } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';

export type ProjectedDividendEvent = {
    eventId: string;
    symbol: string;
    exDate?: string | null;
    paymentDate?: string | null;
    amountPerShare?: number | null;
    currency?: string | null;
    frequency?: string | null;
    declared?: boolean;
    estimated?: boolean;
    confidence?: 'low' | 'medium' | 'high' | string;
    source?: 'PROJECTED_HISTORY' | string;
};

const COLLECTION = 'dividend_projection_global';
const FIRESTORE_IN_QUERY_LIMIT = 10;

const normalizeDateString = (value: unknown) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed ? trimmed.slice(0, 10) : null;
};

const effectiveDateForEvent = (event: Pick<ProjectedDividendEvent, 'paymentDate' | 'exDate'>) => (
    normalizeDateString(event.paymentDate) || normalizeDateString(event.exDate)
);

const normalizeSymbols = (symbols: string[]) => Array.from(new Set(symbols.map((s) => String(s || '').trim().toUpperCase()).filter(Boolean)));

const chunkSymbols = (symbols: string[]) => {
    const chunks: string[][] = [];
    for (let index = 0; index < symbols.length; index += FIRESTORE_IN_QUERY_LIMIT) chunks.push(symbols.slice(index, index + FIRESTORE_IN_QUERY_LIMIT));
    return chunks;
};

export async function loadProjectedDividendEventsForSymbols({
    db,
    symbols,
}: {
    db: Firestore;
    symbols: string[];
}): Promise<ProjectedDividendEvent[]> {
    const normalizedSymbols = normalizeSymbols(symbols);
    if (!normalizedSymbols.length) return [];

    const symbolSet = new Set(normalizedSymbols);
    const rows = new Map<string, ProjectedDividendEvent>();

    await Promise.all(chunkSymbols(normalizedSymbols).map(async (symbolChunk) => {
        const snapshot = await getDocs(query(collection(db, COLLECTION), where('symbol', 'in', symbolChunk)));
        snapshot.docs.forEach((doc) => {
            const data = doc.data() || {};
            const symbol = String(data.symbol || '').trim().toUpperCase();
            const eventId = String(data.eventId || doc.id || '').trim();
            if (!symbolSet.has(symbol) || !eventId || !effectiveDateForEvent(data as ProjectedDividendEvent)) return;

            rows.set(eventId, {
                eventId,
                symbol,
                exDate: normalizeDateString(data.exDate),
                paymentDate: normalizeDateString(data.paymentDate),
                amountPerShare: typeof data.amountPerShare === 'number' ? data.amountPerShare : null,
                currency: typeof data.currency === 'string' ? data.currency : null,
                frequency: typeof data.frequency === 'string' ? data.frequency : null,
                declared: typeof data.declared === 'boolean' ? data.declared : false,
                estimated: typeof data.estimated === 'boolean' ? data.estimated : true,
                confidence: typeof data.confidence === 'string' ? data.confidence : 'low',
                source: typeof data.source === 'string' ? data.source : 'PROJECTED_HISTORY',
            });
        });
    }));

    return Array.from(rows.values()).sort((a, b) => (effectiveDateForEvent(a) || '').localeCompare(effectiveDateForEvent(b) || '') || a.symbol.localeCompare(b.symbol));
}
